import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Sparkles, ShieldCheck, Package, Bell } from 'lucide-react';

const Section = ({ id, title, eyebrow, children }) => (
  <section id={id} className="py-12 border-b border-border" data-testid={`style-section-${id}`}>
    <p className="font-manrope text-xs tracking-widest uppercase text-muted-foreground mb-2">{eyebrow}</p>
    <h2 className="font-playfair text-3xl sm:text-4xl mb-8">{title}</h2>
    {children}
  </section>
);

const Swatch = ({ name, hex, note, textOnSwatch = '#020412' }) => (
  <div className="rounded-xl overflow-hidden border border-border bg-card" data-testid={`swatch-${name}`}>
    <div className="h-24 flex items-end p-3" style={{ background: hex, color: textOnSwatch }}>
      <span className="font-manrope text-xs tracking-widest uppercase">{name}</span>
    </div>
    <div className="p-3">
      <p className="font-manrope text-sm">{hex}</p>
      {note && <p className="text-xs text-muted-foreground mt-1">{note}</p>}
    </div>
  </div>
);

const TypeRow = ({ label, className, sample = 'The quiet weight of reclaimed oak.' }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-8 py-4 border-b border-border last:border-0">
    <div className="sm:w-48 shrink-0">
      <p className="font-manrope text-xs tracking-widest uppercase text-muted-foreground">{label}</p>
      <p className="font-manrope text-xs text-muted-foreground mt-1">{className}</p>
    </div>
    <p className={className}>{sample}</p>
  </div>
);

export default function StyleGuidePage() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="style-guide-page">
      {/* Hero */}
      <header className="max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-16">
        <p className="font-manrope text-xs tracking-widest uppercase text-muted-foreground mb-4">
          Brand · Visual System · v1
        </p>
        <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl mb-6">
          The <span className="text-gold-gradient">PatinaMart</span> style guide
        </h1>
        <p className="font-manrope text-base sm:text-lg text-muted-foreground max-w-2xl">
          A living reference for the colours, type, components and motion that make PatinaMart feel
          like one product. The full written rules live at{' '}
          <code className="text-accent">/app/memory/BRAND_GUIDELINES.md</code>.
        </p>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Wordmark */}
        <Section id="wordmark" eyebrow="01 — Wordmark" title="The mark, two letterforms">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-card border border-border rounded-xl p-10 flex items-center justify-center">
              <div className="flex items-center gap-2">
                <span className="font-playfair text-5xl font-bold text-gold-gradient">Patina</span>
                <span className="font-manrope text-base text-muted-foreground tracking-widest uppercase">Mart</span>
              </div>
            </div>
            <div className="bg-card border border-border rounded-xl p-10 flex flex-col items-center justify-center gap-4">
              <img src="/favicon.svg" alt="PatinaMart favicon" className="w-20 h-20" data-testid="style-favicon" />
              <p className="font-manrope text-xs tracking-widest uppercase text-muted-foreground">Favicon · /favicon.svg</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-4 max-w-3xl">
            <strong className="text-foreground">Patina</strong> is always Playfair Display 700 in the gold gradient.{' '}
            <strong className="text-foreground">Mart</strong> is always Manrope, uppercase, tracked-out, in muted slate.
            The favicon mirrors both letterforms — gold serif <em>P</em>, slate sans <em>M</em>.
          </p>
        </Section>

        {/* Colour */}
        <Section id="colour" eyebrow="02 — Colour" title="Navy first. Gold sparingly.">
          <h3 className="font-playfair text-xl mb-4">Surfaces</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <Swatch name="Background" hex="#020412" textOnSwatch="#F1F5F9" note="--background" />
            <Swatch name="Card" hex="#0F132A" textOnSwatch="#F1F5F9" note="--card" />
            <Swatch name="Border / Muted" hex="#1F293B" textOnSwatch="#F1F5F9" note="--border / --muted" />
            <Swatch name="Foreground" hex="#F1F5F9" note="--foreground" />
          </div>

          <h3 className="font-playfair text-xl mb-4">Brand accents</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Swatch name="Gold start" hex="#D4AF37" note="gradient 0%" />
            <Swatch name="Gold peak" hex="#FCD34D" note="gradient 50%" />
            <Swatch name="Gold deep" hex="#B45309" textOnSwatch="#F1F5F9" note="gradient 100%" />
            <Swatch name="Royal Purple" hex="#5B21B6" textOnSwatch="#F1F5F9" note="primary CTA" />
            <Swatch name="Royal Deep" hex="#4C1D95" textOnSwatch="#F1F5F9" note="CTA end" />
          </div>

          <div
            className="rounded-xl p-10 mb-10 flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #FCD34D 50%, #B45309 100%)' }}
            data-testid="gold-gradient-block"
          >
            <p className="font-playfair text-2xl text-[#020412]">linear-gradient(135deg, #D4AF37, #FCD34D, #B45309)</p>
          </div>

          <h3 className="font-playfair text-xl mb-4">Status</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Swatch name="Available" hex="#10B981" textOnSwatch="#020412" note=".badge-available" />
            <Swatch name="Pending" hex="#F59E0B" textOnSwatch="#020412" note=".badge-pending" />
            <Swatch name="Sold" hex="#EF4444" textOnSwatch="#F1F5F9" note=".badge-sold" />
            <Swatch name="Info" hex="#3B82F6" textOnSwatch="#F1F5F9" note=".condition-good" />
          </div>
        </Section>

        {/* Typography */}
        <Section id="typography" eyebrow="03 — Typography" title="Three families, one job each">
          <div className="bg-card border border-border rounded-xl px-6">
            <TypeRow label="H1 / Hero" className="font-playfair text-4xl sm:text-5xl lg:text-6xl" />
            <TypeRow label="H2 / Section" className="font-playfair text-3xl sm:text-4xl" />
            <TypeRow label="H3 / Card title" className="font-playfair text-xl sm:text-2xl" />
            <TypeRow label="Eyebrow / Nav" className="font-manrope text-sm tracking-widest uppercase text-muted-foreground" sample="BROWSE THE MARKETPLACE" />
            <TypeRow label="Body" className="font-manrope text-base" />
            <TypeRow label="Body small" className="font-manrope text-sm text-muted-foreground" />
            <TypeRow label="Caption" className="text-xs text-muted-foreground" sample="Listed 3 days ago · East London" />
            <TypeRow label="Editorial pull" className="font-cormorant text-2xl italic" sample="“Every reclaimed beam is a story we’d rather not let go.”" />
          </div>
        </Section>

        {/* Buttons */}
        <Section id="buttons" eyebrow="04 — Buttons" title="One gold per screen">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <p className="font-manrope text-xs tracking-widest uppercase text-muted-foreground mb-4">Primary — gold</p>
              <button className="btn-gold px-6 py-3 rounded-lg w-full" data-testid="btn-gold-sample">
                List your salvage
              </button>
              <p className="text-xs text-muted-foreground mt-3">For the single highest-priority CTA on a screen.</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6">
              <p className="font-manrope text-xs tracking-widest uppercase text-muted-foreground mb-4">Secondary — purple</p>
              <button className="btn-primary px-6 py-3 rounded-lg w-full text-foreground" data-testid="btn-primary-sample">
                View marketplace
              </button>
              <p className="text-xs text-muted-foreground mt-3">Supporting CTAs and section links.</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6">
              <p className="font-manrope text-xs tracking-widest uppercase text-muted-foreground mb-4">Outline (shadcn)</p>
              <Button variant="outline" className="w-full" data-testid="btn-outline-sample">Save for later</Button>
              <p className="text-xs text-muted-foreground mt-3">Tertiary actions inside cards.</p>
            </div>
          </div>
        </Section>

        {/* Badges */}
        <Section id="badges" eyebrow="05 — Badges" title="Status + trust">
          <div className="flex flex-wrap gap-3">
            <span className="badge-available px-3 py-1 rounded-full text-xs font-manrope tracking-wide" data-testid="badge-available">Available</span>
            <span className="badge-pending px-3 py-1 rounded-full text-xs font-manrope tracking-wide" data-testid="badge-pending">Pending</span>
            <span className="badge-sold px-3 py-1 rounded-full text-xs font-manrope tracking-wide" data-testid="badge-sold">Sold</span>
            <span className="px-3 py-1 rounded-full text-xs font-manrope tracking-wide bg-accent/15 text-accent border border-accent/40 inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Verified seller
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-manrope tracking-wide bg-accent/15 text-accent border border-accent/40 inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Featured
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-manrope tracking-wide bg-muted/40 text-muted-foreground border border-border inline-flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5" /> External listing
            </span>
          </div>
        </Section>

        {/* Cards */}
        <Section id="cards" eyebrow="06 — Cards" title="Solid surfaces, lifted on hover">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div key={`sample-card-${n}`} className="bg-card border border-border rounded-xl p-6 card-hover" data-testid={`card-sample-${n}`}>
                <div className="w-10 h-10 rounded-lg bg-accent/15 text-accent flex items-center justify-center mb-4">
                  <Bell className="w-5 h-5" />
                </div>
                <h3 className="font-playfair text-xl mb-2">Reclaimed material #{n}</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  A standard PatinaMart card — `bg-card border border-border rounded-xl p-6` with `.card-hover`.
                </p>
                <Link to="/marketplace" className="text-accent text-sm font-manrope tracking-wide uppercase">
                  Browse →
                </Link>
              </div>
            ))}
          </div>
        </Section>

        {/* Inputs */}
        <Section id="inputs" eyebrow="07 — Inputs" title="Gold focus ring, navy field">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
            <div>
              <label className="font-manrope text-xs tracking-widest uppercase text-muted-foreground block mb-2">Email</label>
              <input
                type="email"
                placeholder="you@studio.com"
                className="w-full bg-secondary border border-border rounded-lg px-4 py-3 text-foreground"
                data-testid="style-input-email"
              />
            </div>
            <div>
              <label className="font-manrope text-xs tracking-widest uppercase text-muted-foreground block mb-2">Postcode radius (km)</label>
              <input
                type="number"
                defaultValue={25}
                className="w-full bg-secondary border border-border rounded-lg px-4 py-3 text-foreground"
                data-testid="style-input-radius"
              />
            </div>
          </div>
        </Section>

        {/* Motion */}
        <Section id="motion" eyebrow="08 — Motion" title="Restrained, gold-tinted">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card border border-border rounded-xl p-6 animate-fadeIn" data-testid="motion-fadein">
              <p className="font-playfair text-xl mb-2">fadeIn</p>
              <p className="text-sm text-muted-foreground">0.5s ease-out. Page-load reveals.</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6 animate-slideUp stagger-2" data-testid="motion-slideup">
              <p className="font-playfair text-xl mb-2">slideUp</p>
              <p className="text-sm text-muted-foreground">0.5s ease-out, stagger-1…5 for hero rows.</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6 relative overflow-hidden" data-testid="motion-shimmer">
              <p className="font-playfair text-xl mb-2">shimmer</p>
              <p className="text-sm text-muted-foreground">Gold-tinted skeletons.</p>
              <div className="mt-4 h-3 w-full rounded animate-shimmer bg-secondary" />
            </div>
          </div>
        </Section>

        {/* Don'ts */}
        <Section id="donts" eyebrow="09 — The slop filter" title="What we never ship">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl">
            {[
              'Purple-to-pink gradients on white backgrounds',
              'Inter / Roboto / Arial as headline fonts',
              'Equal-spaced centred grids of identical cards',
              'Emoji icons (use lucide-react)',
              'More than one gradient per viewport',
              'Gold on gold without a navy carrier',
              '"Click here" / "Submit" copy — be specific',
              'Drop shadows on imagery (the card is the frame)',
            ].map((rule) => (
              <li key={rule} className="bg-card border border-border rounded-lg px-4 py-3 text-sm text-muted-foreground">
                ✕ &nbsp; {rule}
              </li>
            ))}
          </ul>
        </Section>

        <div className="py-16 text-center">
          <p className="font-manrope text-xs tracking-widest uppercase text-muted-foreground">
            Last updated · Feb 2026 · synced with{' '}
            <code className="text-accent">/app/memory/BRAND_GUIDELINES.md</code>
          </p>
        </div>
      </div>
    </div>
  );
}
